"use client";

import { cn } from "@/lib/utils/index";
import * as Menubar from "@radix-ui/react-menubar";
import { ChevronRight } from "lucide-react";
import { FETCH_MENUBAR_MENU_VALUE, MENU_CONTENT_MOTION_CLASS, PICKER_POPUP_Z_INDEX } from "./_logic/constants";

interface FetchMenubarItem {
  label: string | React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
}

interface FetchMenubarSubMenu {
  label: string | React.ReactNode;
  items: FetchMenubarItem[];
  disabled?: boolean;
  hideChevron?: boolean;
}

interface FetchMenubarSeparator {
  separator: true;
}

/**
 * Optional controlled open state. `value` and `onValueChange` come together or not at all:
 * Escape, an outside click and picking an item close the menu only by calling
 * `onValueChange("")`, so a `value` with no handler would leave the menu stuck open.
 */
type FetchMenubarOpenState =
  | {
      /** `FETCH_MENUBAR_MENU_VALUE` opens the menu; `""` closes it. */
      value: string;
      /** Called with the menu value when it opens and `""` when it closes. */
      onValueChange: (value: string) => void;
    }
  | { value?: never; onValueChange?: never };

type FetchMenubarProps = FetchMenubarOpenState & {
  trigger: React.ReactNode;
  items: (FetchMenubarItem | FetchMenubarSubMenu | FetchMenubarSeparator)[];
  className?: string;
  contentClassName?: string;
  /** Extra classes for every item and submenu trigger (e.g. rounder highlight corners). */
  itemClassName?: string;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
};

const isSubMenu = (
  item: FetchMenubarItem | FetchMenubarSubMenu | FetchMenubarSeparator
): item is FetchMenubarSubMenu => {
  return "items" in item;
};

const isSeparator = (
  item: FetchMenubarItem | FetchMenubarSubMenu | FetchMenubarSeparator
): item is FetchMenubarSeparator => {
  return "separator" in item;
};

/**
 * Nowadays themed menubar component using Radix UI. Uncontrolled by default; pass `value` and
 * `onValueChange` together to open or close its menu from outside.
 *
 * @param props - Trigger, items, placement, and the optional controlled open state.
 * @returns The menubar.
 */
export const FetchMenubar = ({
  trigger,
  items,
  className,
  contentClassName,
  itemClassName,
  align = "end",
  side = "bottom",
  sideOffset = 4,
  value,
  onValueChange,
}: FetchMenubarProps) => {
  return (
    <Menubar.Root className={cn("flex", className)} value={value} onValueChange={onValueChange}>
      <Menubar.Menu value={FETCH_MENUBAR_MENU_VALUE}>
        <Menubar.Trigger asChild>{trigger}</Menubar.Trigger>
        <Menubar.Portal>
          <Menubar.Content
            align={align}
            side={side}
            sideOffset={sideOffset}
            style={{ zIndex: PICKER_POPUP_Z_INDEX }}
            className={cn(
              "min-w-[12rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-lg",
              MENU_CONTENT_MOTION_CLASS,
              contentClassName
            )}
          >
            {items.map((item, index) => {
              if (isSeparator(item)) {
                return <Menubar.Separator key={`separator-${index}`} className="mx-1 my-1 h-px bg-gray-200" />;
              }

              if (isSubMenu(item)) {
                return (
                  <Menubar.Sub key={index}>
                    <Menubar.SubTrigger
                      className={cn(
                        "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-medium outline-none",
                        "focus:bg-gray-100 focus:text-gray-900 transition-colors duration-150",
                        "data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900",
                        item.disabled && "pointer-events-none opacity-50",
                        itemClassName
                      )}
                      disabled={item.disabled}
                    >
                      {item.label}
                      {!item.hideChevron && <ChevronRight className="ml-auto h-4 w-4" />}
                    </Menubar.SubTrigger>
                    <Menubar.Portal>
                      <Menubar.SubContent
                        style={{ zIndex: PICKER_POPUP_Z_INDEX }}
                        className={cn(
                          "min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-lg",
                          MENU_CONTENT_MOTION_CLASS,
                          // Submenus portal separately, so the root panel's classes are applied here too.
                          contentClassName
                        )}
                      >
                        {item.items.map((subItem, subIndex) => (
                          <Menubar.Item
                            key={subIndex}
                            className={cn(
                              "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-medium outline-none",
                              "focus:bg-gray-100 focus:text-gray-900 transition-colors duration-150",
                              "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                              itemClassName
                            )}
                            disabled={subItem.disabled}
                            onClick={subItem.onClick}
                          >
                            {subItem.label}
                            {subItem.shortcut && (
                              <span className="ml-auto text-xs tracking-widest text-gray-500">{subItem.shortcut}</span>
                            )}
                          </Menubar.Item>
                        ))}
                      </Menubar.SubContent>
                    </Menubar.Portal>
                  </Menubar.Sub>
                );
              }

              return (
                <Menubar.Item
                  key={index}
                  className={cn(
                    "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-medium outline-none",
                    "focus:bg-gray-100 focus:text-gray-900 transition-colors duration-150",
                    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                    itemClassName
                  )}
                  disabled={item.disabled}
                  onClick={item.onClick}
                >
                  {item.label}
                  {item.shortcut && (
                    <span className="ml-auto text-xs tracking-widest text-gray-500">{item.shortcut}</span>
                  )}
                </Menubar.Item>
              );
            })}
          </Menubar.Content>
        </Menubar.Portal>
      </Menubar.Menu>
    </Menubar.Root>
  );
};
